import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  BarChart3, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Users, 
  Calendar, 
  TrendingUp, 
  AlertTriangle, 
  Info, 
  Loader2, 
  Eye, 
  FileText, 
  User, 
  Building 
} from 'lucide-react';
import apiService from '@/services/api';
import LoteDisparosTable from '@/components/LoteDisparosTable';

interface MetricsDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  metricType: 'total' | 'enviados' | 'falhas' | 'pendentes';
  title: string;
  icon: React.ComponentType<any>;
  color: string;
  clientId?: number; // Filtro por cliente específico
  period?: string; // Filtro de período do dashboard
  customStartDate?: string; // Data de início personalizada
  customEndDate?: string; // Data de fim personalizada
}

interface ClientData {
  client_id: number;
  client_name: string;
  count: number;
  percentage: number;
  value?: number;
}

interface BatchData {
  lote_id: string;
  client_name: string;
  count: number;
  percentage: number;
  status: string;
  created_at: string;
  value?: number;
  client_id: number;
}

interface DetailedBatchData {
  lote_id: string;
  lote_id_formatted?: string;
  client_name: string;
  client_id: number;
  count: number;
  percentage: number;
  status: string;
  created_at: string;
  value?: number;
  error_details?: Array<{
    motivo_rejeicao: string;
    count: number;
    total_value: number;
  }>;
}

interface DetailedData {
  clients: ClientData[];
  batches: DetailedBatchData[];
  total: number;
  summary: {
    total_clients: number;
    total_batches: number;
    average_per_client: number;
    top_client: string;
    success_rate?: number;
    failure_rate?: number;
  };
  // Novos campos para separar pendentes, fila e contatos inválidos
  pendentes?: DetailedBatchData[];
  fila?: DetailedBatchData[];
  contatos_invalidos?: DetailedBatchData[];
}

const MetricsDetailModal: React.FC<MetricsDetailModalProps> = ({
  isOpen,
  onClose,
  metricType,
  title,
  icon: Icon,
  color,
  clientId,
  period,
  customStartDate,
  customEndDate
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DetailedData | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<DetailedBatchData | null>(null);
  const [showBatchDetails, setShowBatchDetails] = useState(false);

  const fetchDetailedData = async () => {
    setLoading(true);
    try {
      // Construir parâmetros da query
      const params = new URLSearchParams();
      if (clientId) {
        params.append('client_id', clientId.toString());
      }
      
      // Adicionar filtros de período
      if (period) {
        params.append('period', period);
      }
      
      if (customStartDate) {
        params.append('start_date', customStartDate);
      }
      
      if (customEndDate) {
        params.append('end_date', customEndDate);
      }
      
      const queryString = params.toString();
      const url = `/analytics/disparos/detailed/${metricType}${queryString ? `?${queryString}` : ''}`;
      
      console.log('🔍 [MetricsDetailModal] URL construída:', url);
      console.log('🔍 [MetricsDetailModal] Filtros aplicados:', { period, customStartDate, customEndDate, clientId });
      console.log('🔍 [MetricsDetailModal] API_BASE_URL:', import.meta.env.DEV ? 'vazio (dev)' : 'não-vazio (prod)');
      
      const response = await apiService.get(url);
      console.log('🔍 [MetricsDetailModal] Resposta da API:', response);
      
      if (response.success && response.data) {
        console.log('🔍 [MetricsDetailModal] Dados recebidos:', response.data);
        console.log('🔍 [MetricsDetailModal] Lotes recebidos:', response.data.batches);
        if (response.data.batches && response.data.batches.length > 0) {
          console.log('🔍 [MetricsDetailModal] Primeiros 5 lotes antes da ordenação:', response.data.batches.slice(0, 5).map(b => ({
            lote_id: b.lote_id,
            client_name: b.client_name,
            count: b.count
          })));
          
          // Aplicar ordenação global por número de lote
          const sortedBatches = response.data.batches.sort((a: any, b: any) => {
            // Extrair número do lote_id (ex: "Lote #356" ou "356" -> 356)
            const getLoteNumber = (loteId: string) => {
              // Remove qualquer texto antes do número e converte para inteiro
              const match = String(loteId).match(/#?(\d+)/);
              return match ? parseInt(match[1], 10) : 0;
            };
            
            const loteA = getLoteNumber(a.lote_id);
            const loteB = getLoteNumber(b.lote_id);
            
            console.log(`🔍 [MetricsDetailModal] Comparando: Lote ${loteA} vs Lote ${loteB}`);
            
            // Ordenação decrescente (maior número primeiro)
            return loteB - loteA;
          });
          
          console.log('🔍 [MetricsDetailModal] Primeiros 5 lotes após ordenação:', sortedBatches.slice(0, 5).map(b => ({
            lote_id: b.lote_id,
            client_name: b.client_name,
            count: b.count
          })));
          
          // Atualizar dados com lotes ordenados
          setData({
            ...response.data,
            batches: sortedBatches
          });
        } else {
          setData(response.data);
        }
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (error) {
      console.error('❌ [MetricsDetailModal] Erro ao buscar dados detalhados:', error);
      console.error('❌ [MetricsDetailModal] URL da requisição:', url);
      console.error('❌ [MetricsDetailModal] Erro completo:', error);
      
      // Não usar dados mock - mostrar estado vazio
      setData({
        clients: [],
        batches: [],
        total: 0,
        summary: {
          total_clients: 0,
          total_batches: 0,
          average_per_client: 0,
          top_client: 'Nenhum',
          success_rate: 0,
          failure_rate: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDetailedData();
    }
  }, [isOpen, metricType, clientId, period, customStartDate, customEndDate]);

  const handleViewBatchDetails = (batch: DetailedBatchData) => {
    setSelectedBatch(batch);
    setShowBatchDetails(true);
  };

  const handleCloseBatchDetails = () => {
    setSelectedBatch(null);
    setShowBatchDetails(false);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enviando':
        return 'bg-blue-100 text-blue-800';
      case 'concluído':
        return 'bg-green-100 text-green-800';
      case 'falha':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getMetricIcon = () => {
    switch (metricType) {
      case 'total':
        return <BarChart3 className="w-5 h-5" />;
      case 'enviados':
        return <CheckCircle2 className="w-5 h-5" />;
      case 'falhas':
        return <XCircle className="w-5 h-5" />;
      case 'pendentes':
        return <Clock className="w-5 h-5" />;
      default:
        return <BarChart3 className="w-5 h-5" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${color}`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            Detalhes de {title}
          </DialogTitle>
          <DialogDescription>
            Análise detalhada dos dados de {title.toLowerCase()} com informações por cliente e lote
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="ml-2">Carregando dados detalhados...</span>
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Resumo Geral */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5" />
                  Resumo Geral
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {formatNumber(data.total)}
                    </div>
                    <div className="text-sm text-gray-600">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {data.summary.total_clients}
                    </div>
                    <div className="text-sm text-gray-600">Clientes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {data.summary.total_batches}
                    </div>
                    <div className="text-sm text-gray-600">Lotes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {formatNumber(data.summary.average_per_client)}
                    </div>
                    <div className="text-sm text-gray-600">Média/Cliente</div>
                  </div>
                </div>
                
                {data.summary.success_rate && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        Taxa de Sucesso: {data.summary.success_rate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}

                {data.summary.failure_rate && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium text-red-800">
                        Taxa de Falha: {data.summary.failure_rate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Seção Unificada - Clientes e Lotes */}
            {/* Renderização especial para pendentes com seções separadas */}
            {metricType === 'pendentes' ? (
              <div className="space-y-6">
                {/* Seção Pendentes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-500" />
                      Pendentes por Cliente e Lote
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(!data.pendentes || data.pendentes.length === 0) ? (
                      <div className="text-center py-8">
                        <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-600">Nenhum item pendente no período selecionado.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {data.pendentes.map((batch, index) => (
                          <div key={`pendente-${batch.client_id}-${batch.lote_id}`} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center text-sm font-medium text-yellow-600">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-gray-500" />
                                    <span className="font-medium">{batch.client_name}</span>
                                  </div>
                                  <div className="text-sm text-gray-600">ID: {batch.client_id}</div>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <FileText className="w-3 h-3" />
                                  <span>Lote: {batch.lote_id_formatted || batch.lote_id}</span>
                                  <span className="text-gray-400">•</span>
                                  <span>Criado em: {batch.created_at}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className="text-2xl font-bold text-gray-900">
                                  {formatNumber(batch.count)}
                                </div>
                                <div className="text-sm text-gray-600">pendentes</div>
                                {batch.value && (
                                  <div className="text-sm text-green-600 font-medium mt-1">
                                    {formatCurrency(batch.value)}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-2">
                                <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">
                                  PENDENTE
                                </Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewBatchDetails(batch)}
                                  className="flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  Ver Detalhes
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Seção Fila */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-500" />
                      Em Fila por Cliente e Lote
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(!data.fila || data.fila.length === 0) ? (
                      <div className="text-center py-8">
                        <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-600">Nenhum item em fila no período selecionado.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {data.fila.map((batch, index) => (
                          <div key={`fila-${batch.client_id}-${batch.lote_id}`} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-gray-500" />
                                    <span className="font-medium">{batch.client_name}</span>
                                  </div>
                                  <div className="text-sm text-gray-600">ID: {batch.client_id}</div>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <FileText className="w-3 h-3" />
                                  <span>Lote: {batch.lote_id_formatted || batch.lote_id}</span>
                                  <span className="text-gray-400">•</span>
                                  <span>Criado em: {batch.created_at}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className="text-2xl font-bold text-gray-900">
                                  {formatNumber(batch.count)}
                                </div>
                                <div className="text-sm text-gray-600">em fila</div>
                                {batch.value && (
                                  <div className="text-sm text-green-600 font-medium mt-1">
                                    {formatCurrency(batch.value)}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-2">
                                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">
                                  FILA
                                </Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewBatchDetails(batch)}
                                  className="flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  Ver Detalhes
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Seção Contatos Inválidos */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-500" />
                      Contatos Inválidos por Cliente e Lote
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(!data.contatos_invalidos || data.contatos_invalidos.length === 0) ? (
                      <div className="text-center py-8">
                        <XCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-600">Nenhum contato inválido no período selecionado.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {data.contatos_invalidos.map((batch, index) => (
                          <div key={`invalido-${batch.client_id}-${batch.lote_id}`} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-sm font-medium text-red-600">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-gray-500" />
                                    <span className="font-medium">{batch.client_name}</span>
                                  </div>
                                  <div className="text-sm text-gray-600">ID: {batch.client_id}</div>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <FileText className="w-3 h-3" />
                                  <span>Lote: {batch.lote_id_formatted || batch.lote_id}</span>
                                  <span className="text-gray-400">•</span>
                                  <span>Criado em: {batch.created_at}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className="text-2xl font-bold text-gray-900">
                                  {formatNumber(batch.count)}
                                </div>
                                <div className="text-sm text-gray-600">inválidos</div>
                                {batch.value && (
                                  <div className="text-sm text-green-600 font-medium mt-1">
                                    {formatCurrency(batch.value)}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-2">
                                <Badge className="bg-red-100 text-red-800 hover:bg-red-200">
                                  INVÁLIDO
                                </Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewBatchDetails(batch)}
                                  className="flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  Ver Detalhes
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              /* Renderização padrão para outros tipos de métrica */
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    {metricType === 'falhas' ? 'Falhas por Cliente e Lote' : 
                     metricType === 'enviados' ? 'Enviados por Cliente e Lote' :
                     'Total por Cliente e Lote'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.batches.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        {metricType === 'falhas' && <XCircle className="w-8 h-8 text-gray-400" />}
                        {metricType === 'enviados' && <CheckCircle2 className="w-8 h-8 text-gray-400" />}
                        {metricType === 'total' && <BarChart3 className="w-8 h-8 text-gray-400" />}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Nenhum {metricType} encontrado
                      </h3>
                      <p className="text-gray-600">
                        {metricType === 'falhas' ? 'Nenhuma falha registrada no período selecionado.' :
                         metricType === 'enviados' ? 'Nenhum envio realizado no período selecionado.' :
                         'Nenhum dado disponível para o período selecionado.'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {data.batches.map((batch, index) => (
                        <div key={`${batch.client_id}-${batch.lote_id}`} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-gray-500" />
                                  <span className="font-medium">{batch.client_name}</span>
                                </div>
                                <div className="text-sm text-gray-600">ID: {batch.client_id}</div>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <FileText className="w-3 h-3" />
                                <span>Lote: {batch.lote_id_formatted || batch.lote_id}</span>
                                <span className="text-gray-400">•</span>
                                <span>Criado em: {batch.created_at}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-gray-900">
                                {formatNumber(batch.count)}
                              </div>
                              <div className="text-sm text-gray-600">
                                {metricType === 'falhas' ? 'falhas' :
                                 metricType === 'enviados' ? 'enviados' : 'total'}
                              </div>
                              {batch.value && (
                                <div className="text-sm text-green-600 font-medium mt-1">
                                  {formatCurrency(batch.value)}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-2">
                              <Badge className={getStatusColor(batch.status)}>
                                {batch.status}
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewBatchDetails(batch)}
                                className="flex items-center gap-1"
                              >
                                <Eye className="w-3 h-3" />
                                Ver Detalhes
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Botão de Fechar */}
            <div className="flex justify-end">
              <Button onClick={onClose} variant="outline">
                Fechar
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <p className="text-gray-600">Nenhum dado disponível para {title.toLowerCase()}</p>
          </div>
        )}
      </DialogContent>

      {/* Modal de Detalhes do Lote */}
      <Dialog open={showBatchDetails} onOpenChange={setShowBatchDetails}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <FileText className="w-6 h-6" />
              Detalhes do Lote: {selectedBatch?.lote_id}
            </DialogTitle>
            <DialogDescription>
              Análise detalhada dos erros e falhas do lote {selectedBatch?.lote_id}
            </DialogDescription>
          </DialogHeader>

          {selectedBatch && (
            <div className="space-y-6">
              {/* Informações do Lote */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Informações do Lote
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">
                        {formatNumber(selectedBatch.count)}
                      </div>
                      <div className="text-sm text-gray-600">Total de {metricType}</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {(selectedBatch.percentage || 0).toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Percentual</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {selectedBatch.client_name}
                      </div>
                      <div className="text-sm text-gray-600">Cliente</div>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">
                        {selectedBatch.created_at}
                      </div>
                      <div className="text-sm text-gray-600">Data de Criação</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Detalhes de Erros (apenas para falhas) */}
              {metricType === 'falhas' && selectedBatch.error_details && selectedBatch.error_details.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Detalhes dos Erros
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {selectedBatch.error_details.map((error, index) => (
                        <div key={index} className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-sm font-medium text-red-600">
                              {index + 1}
                            </div>
                            <div>
                              <div className="font-medium text-red-800">
                                {error.motivo_rejeicao}
                              </div>
                              <div className="text-sm text-red-600">
                                {formatNumber(error.count)} ocorrências
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-red-600">
                              {formatCurrency(error.total_value)}
                            </div>
                            <div className="text-sm text-red-500">
                              Valor total afetado
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Resumo para outros tipos de métrica */}
              {metricType !== 'falhas' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      Resumo do Lote
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        {metricType === 'enviados' && <CheckCircle2 className="w-8 h-8 text-green-600" />}
                        {metricType === 'pendentes' && <Clock className="w-8 h-8 text-yellow-600" />}
                        {metricType === 'total' && <BarChart3 className="w-8 h-8 text-blue-600" />}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Lote {selectedBatch.lote_id}
                      </h3>
                      <p className="text-gray-600">
                        {formatNumber(selectedBatch.count)} {metricType} registrados
                      </p>
                      {selectedBatch.value && (
                        <p className="text-green-600 font-medium mt-2">
                          Valor total: {formatCurrency(selectedBatch.value)}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Detalhes dos Disparos do Lote */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Detalhes dos Disparos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    console.log('🔍 [MetricsDetailModal] Passando props para LoteDisparosTable:', {
                      loteId: selectedBatch.lote_id,
                      metricType,
                      clientId: selectedBatch.client_id,
                      selectedBatch: selectedBatch
                    });
                    return (
                      <LoteDisparosTable 
                        loteId={selectedBatch.lote_id} 
                        metricType={metricType}
                        clientId={selectedBatch.client_id}
                        initialStatusFilter={selectedBatch.status}
                      />
                    );
                  })()}
                </CardContent>
              </Card>

            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default MetricsDetailModal;
